import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Pagination from "../common/Pagination";

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState({});
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 9;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const excludedCategories = ["Paintings", "Quiz", "Poll", "Quotes"];

  useEffect(() => {
    fetch("https://parmazing.net/wp-json/wp/v2/posts?per_page=100&_embed")
      .then((res) => res.json())
      .then((data) => {
        setPosts(data);
        return data;
      })
      .then((posts) => {
        const categoryIds = [...new Set(posts.flatMap((post) => post.categories))];
        return fetch(
          `https://parmazing.net/wp-json/wp/v2/categories?include=${categoryIds.join(",")}`
        );
      })
      .then((res) => res.json())
      .then((data) => {
        const categoryMap = {};
        data.forEach((category) => {
          categoryMap[category.id] = category.name;
        });
        setCategories(categoryMap);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p className="text-yellow-600 font-semibold animate-pulse text-lg">
          Loading...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p className="text-red-600 font-semibold text-lg">Error: {error}</p>
      </div>
    );
  }

  // categorize posts while excluding some categories
  const categorizedPosts = {};
  posts.forEach((post) => {
    post.categories.forEach((categoryId) => {
      const categoryName = categories[categoryId] || "Uncategorized";
      if (!excludedCategories.includes(categoryName)) {
        if (!categorizedPosts[categoryName]) {
          categorizedPosts[categoryName] = [];
        }
        categorizedPosts[categoryName].push(post);
      }
    });
  });

  const availableCategories = Object.keys(categorizedPosts);

  const displayedPosts =
    selectedCategory === "All"
      ? posts.filter((post) =>
          post.categories.some(
            (categoryId) => !excludedCategories.includes(categories[categoryId])
          )
        )
      : categorizedPosts[selectedCategory] || [];

  const totalPages = Math.ceil(displayedPosts.length / postsPerPage);
  const paginatedPosts = displayedPosts.slice(
    (currentPage - 1) * postsPerPage,
    currentPage * postsPerPage
  );

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setCurrentPage(1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // safe helper to get featured image URL from _embedded
  const getFeaturedImage = (post) => {
    return (
      post._embedded?.["wp:featuredmedia"]?.[0]?.source_url ||
      "Parmazing_logo.png"
    );
  };

  const getReadingTimeText = (post) => {
    // if WP or plugin provides a reading_time field, prefer it
    const provided = (post.reading_time || post.meta?.reading_time || "").toString().trim();
    if (provided) {
      // try to clean numeric part
      const num = parseInt(provided.match(/\d+/)?.[0], 10);
      if (!isNaN(num) && num > 0) {
        return `${num} ${num === 1 ? "min read" : "mins read"}`;
      }
      // fallback to provided string
      return provided;
    }

    // fallback: compute from post.content.rendered (strip HTML)
    const html = post.content?.rendered || "";
    const text = html.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "")
                     .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "")
                     .replace(/<\/?[^>]+(>|$)/g, " ")
                     .replace(/\s+/g, " ")
                     .trim();
    if (!text) return ""; // no content

    const words = text.split(/\s+/).length;
    const wpm = 200; // words per minute reading speed
    const minutes = Math.max(1, Math.ceil(words / wpm));
    return `${minutes} ${minutes === 1 ? "min read" : "mins read"}`;
  };

  return (
    <div className="relative min-h-screen flex flex-col items-center px-4 md:px-12 pt-16 md:pt-32 pb-20 bg-gray-100">
      {/* Background Accents */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="w-96 h-96 bg-yellow-200 opacity-20 rounded-full absolute top-10 left-10 transform -translate-x-1/3 -translate-y-1/3"></div>
        <div className="w-72 h-72 bg-yellow-100 opacity-20 rounded-full absolute bottom-10 right-10 transform translate-x-1/3 translate-y-1/3"></div>
      </div>

      {/* Header */}
      <header className="relative z-10 text-center">
        <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 leading-tight">
          Discover <span className="text-yellow-500">Stories</span> & Insights
        </h1>
        <p className="text-base md:text-lg text-gray-700 mt-3 md:mt-4">
          A curated collection of thoughts, ideas, and reflections.
        </p>
      </header>

      {/* Category Filter Bar */}
      <div className="relative z-10 flex flex-wrap justify-center gap-3 md:gap-4 mt-6 md:mt-8 max-h-40 overflow-y-auto">
        <button
          className={`px-5 py-2 md:px-6 md:py-3 rounded-full text-sm font-medium transition ${
            selectedCategory === "All"
              ? "bg-gray-800 text-white"
              : "bg-gray-300 text-gray-800 hover:bg-gray-400"
          }`}
          onClick={() => handleCategoryChange("All")}
        >
          All
        </button>
        {availableCategories.map((category) => (
          <button
            key={category}
            className={`px-5 py-2 md:px-6 md:py-3 rounded-full text-sm font-medium transition ${
              selectedCategory === category
                ? "bg-gray-800 text-white"
                : "bg-gray-300 text-gray-800 hover:bg-gray-400"
            }`}
            onClick={() => handleCategoryChange(category)}
          >
            {category}
          </button>
        ))}
      </div>

      {/* Blog Posts Grid */}
      <div className="relative z-10 grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-10 max-w-6xl">
        {paginatedPosts.map((post) => {
          const featuredUrl = getFeaturedImage(post);
          const readingTimeText = getReadingTimeText(post);

          return (
            <article
              key={post.id}
              className="bg-white rounded-3xl shadow-md transform hover:scale-105 transition duration-300 overflow-hidden"
            >
              {/* Image (top of card) */}
              <div className="w-full h-48 md:h-56 overflow-hidden relative">
                <img
                  src={featuredUrl}
                  alt={post.title.rendered.replace(/<\/?[^>]+(>|$)/g, "")}
                  className="w-full h-full object-cover"
                />
                {/* Top-left category badge */}
                <div className="absolute top-5 left-5 bg-white bg-opacity-85 px-3 py-1 rounded-full text-xs font-medium text-gray-800 shadow-sm">
                  {post.categories
                    .map((id) => categories[id])
                    .filter(Boolean)
                    .slice(0, 1)
                    .join(", ")}
                </div>
              </div>

              {/* Content */}
              <div className="px-6 py-5">
                {/* Title */}
                <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-3 leading-tight break-words">
                  <Link
                    to={`/blog/${post.slug}`}
                    className="hover:text-yellow-600 transition duration-200"
                    dangerouslySetInnerHTML={{ __html: post.title.rendered }}
                  />
                </h2>

                {/* Date + reading time */}
                <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                  <div>
                    <div className="text-xs text-gray-500">
                      {new Date(post.date).toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </div>
                  </div>
                  {readingTimeText && (
                    <div className="text-xs text-gray-500">· {readingTimeText}</div>
                  )}
                </div>

                {/* Excerpt */}
                <div
                  className="text-gray-700 line-clamp-3 mb-4"
                  dangerouslySetInnerHTML={{ __html: post.excerpt.rendered }}
                />

                {/* Read More */}
                <div className="mt-2 flex justify-between items-center">
                  <Link
                    to={`/blog/${post.slug}`}
                    className="inline-block px-4 py-2 bg-yellow-500 text-white font-medium rounded-md hover:bg-yellow-600 transition duration-300"
                  >
                    Read More →
                  </Link>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="relative z-10 mt-10 w-full">
          <div className="flex justify-center overflow-x-auto flex-nowrap scrollbar-hide px-2">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              className="flex flex-nowrap gap-2 sm:gap-3"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Blog;
